import { AppBar, Button, Dialog, DialogActions, DialogContent, FormControl, FormHelperText, IconButton, Switch, TextField, Toolbar, Tooltip, Typography } from '@material-ui/core';
import React from 'react';
import { Add, Close, Delete, Save } from '@material-ui/icons';
import { IDivision } from '../../../../types/model/rights/division';
import GeneralFunctions from '../../../../store/general/functions';
import DivisionFunctions from '../../../../store/rights/division/functions';
import { CROP } from '../../../../appConstants';
import { Transitions } from '../../../customComponents/animations/Transitions';
import StandardFab from '../../../customComponents/button/StandardFab';
import CropAutocomplete from '../../../customComponents/autocomplete/masterData/CropAutocomplete';

interface IDivisionEditDialogProps {
    value : IDivision | null;

    isLoading ?: boolean;

    onClose ?: () => void;
}

interface IDivisionEditDialogState {
    code : string;
    name : string;
    isActive : boolean;
    crops : Array<CROP | string>;
}

export default class DivisionEditDialog extends React.PureComponent<IDivisionEditDialogProps, IDivisionEditDialogState> {
    constructor(props : IDivisionEditDialogProps) {
        super(props);
        this.state = {
            code: '',
            name: '',
            isActive: true,
            crops: [],
        };
    }

    public componentDidUpdate(prevProps : Readonly<IDivisionEditDialogProps>) : void {
        if (!prevProps.value && this.props.value) {
            this.setState({
                crops: [...this.props.value.crops],
                code: this.props.value.code,
                name: this.props.value.name,
                isActive: this.props.value.isActive,
            });
        }
    }

    private readonly onNameChange = (e : React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        this.setState({
            name: e.target.value,
        });
    };

    private readonly onCodeChange = (e : React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        this.setState({
            code: e.target.value,
        });
    };

    private readonly save = async () => {
        try {
            const {
                value,
            } = this.props;
            if (!value) return;
    
            const {
                name,
                isActive,
                crops,
                code,
            } = this.state;

            if (!code) return;
            if (!name) return;
            if (crops.some(x => !x)) return;
    
            await DivisionFunctions.save({
                ...value,
                name,
                code,
                isActive,
                crops: crops as Array<CROP>,
            });

            if (this.props.onClose) this.props.onClose();
            GeneralFunctions.generalShowSuccessSnackbar('Saved');
        } catch (ex) {
            GeneralFunctions.generalShowErrorSnackbar('Error saving division', ex);
        }
    };

    private readonly onSaveClick = () => {
        this.save();
    };

    private readonly onIsActiveChange = () => {
        this.setState({
            isActive: !this.state.isActive,
        });
    };

    private readonly onAddClick = () => {
        const crops = this.state.crops.slice();

        crops.push('');

        this.setState({
            crops,
        });
    };
    
    private readonly onCropChange = (value ?: CROP) => {
        const crops = this.state.crops.slice();
        const index = crops.indexOf(value ?? '');
        crops.splice(index, 1, value ?? '');

        this.setState({
            crops,
        });
    };
    
    private readonly onDeleteClick = (event : React.MouseEvent<HTMLButtonElement>) => {
        const index = Number(event.currentTarget.value);
        const crops = this.state.crops.slice();
        crops.splice(index, 1);

        this.setState({
            crops,
        });
    };

    public render = () => {
        const {
            value,
            isLoading,
            onClose,
        } = this.props;

        const {
            name,
            code,
            isActive,
            crops,
        } = this.state;

        return (
            <Dialog
                open={!!value}
                TransitionComponent={Transitions.Down}
                transitionDuration={500}
                maxWidth={'md'}
                fullWidth
                aria-labelledby='edit-division-dialog-title'
                aria-describedby='edit-division-description'>
                <AppBar className='fdr posr aic jcc bcpd' position='static'>
                    <Toolbar className={'fdr flx1 aic jcc'}>
                        <Typography>
                            {value?.id ? 'EDIT' : 'ADD'} DIVISION
                        </Typography>
                        <span className='flx1' />
                        <Tooltip title='Close'>
                            <div>
                                <IconButton color='inherit' onClick={onClose} aria-label='Close'>
                                    <Close />
                                </IconButton>
                            </div>
                        </Tooltip>
                    </Toolbar>
                </AppBar>
                <DialogContent className='fdc flx1 hfill'>
                    <div className={'fdr ais jcfs'}>
                        <div className={'fdc flx1 jcfs p5 mb10 pr20'} style={{ marginTop: '16px' }}>
                            <FormControl fullWidth>
                                <TextField
                                    autoComplete='off'
                                    required
                                    error={!code}
                                    autoFocus
                                    margin='dense'
                                    id='code'
                                    label='Code'
                                    type='text'
                                    fullWidth
                                    value={code}
                                    onChange={this.onCodeChange}
                                    disabled={isLoading}
                                />
                                {
                                    !code &&
                                    <FormHelperText error>Required</FormHelperText>
                                }
                            </FormControl>
                        </div>
                        <div className={'fdc flx1 jcfs p5 mb10 pr20'} style={{ marginTop: '16px' }}>
                            <FormControl fullWidth>
                                <TextField
                                    autoComplete='off'
                                    required
                                    error={!name}
                                    autoFocus
                                    margin='dense'
                                    id='name'
                                    label='Name'
                                    type='text'
                                    fullWidth
                                    value={name}
                                    onChange={this.onNameChange}
                                    disabled={isLoading}
                                />
                                {
                                    !name &&
                                    <FormHelperText error>Required</FormHelperText>
                                }
                            </FormControl>
                        </div>
                        <div className={'fdr flx1 aic p5 mb10 pr20'} style={{ marginTop: '16px' }}>
                            <Typography className='pr6' >Is Active?</Typography>
                            <Switch
                                id={'isActive'}
                                name={'isActive'}
                                checked={isActive}
                                color='primary'
                                onChange={this.onIsActiveChange}
                                inputProps={{
                                    'aria-label': 'Is Active',
                                    id: 'isActive',
                                }}
                                disabled={isLoading}
                            />
                        </div>
                    </div>
                    <div className={'fdc flx1 p5 mb10'}>
                        <Toolbar>
                            <Typography variant='h5' color='inherit'>
                                CROPS
                            </Typography>
                            <span className={'flx1'} />
                            <StandardFab aria-label='add' disabled={isLoading} onClick={this.onAddClick}>
                                <Add />
                            </StandardFab>
                        </Toolbar>
                        {
                            crops.map((x, i) => (
                                <div key={i} className='fdr ais jcfs mt7'>
                                    <div className='fdc flx1 mr15'>
                                        <CropAutocomplete
                                            key={`${i}`}
                                            required
                                            id={`${i}.name`}
                                            name={`${i}.name`}
                                            fullWidth
                                            value={x}
                                            onChange={this.onCropChange}
                                            disabled={isLoading}
                                        />
                                    </div>
                                    <div className='fdr aic'>
                                        <IconButton disabled={isLoading} className='cpr' size='small' value={i} onClick={this.onDeleteClick}>
                                            <Delete />
                                        </IconButton>
                                    </div>
                                </div>
                            ))
                        }
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button color='primary' disabled={isLoading} onClick={this.onSaveClick}>
                        <Save style={{ marginRight: '10px' }} />
                        SUBMIT
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };
}
